import React, { useEffect, useMemo, useState } from "react";
import { Label, Slider, SliderThumb, SliderTrack } from "react-aria-components";

import BudgetChart from "components/ui-component/BudgetChart";
import { DeleteIcon } from "utils/icon";
import SelectAllDropDown from "components/form/SelectAllDropDown";
import { BUDGET_EXPENSE_OPTION, BUDGET_INCOME_OPTION } from "utils/Options";
import { formatCurrency } from "utils/HelperFunctions";
import CustomAmountInput from "components/ui-component/CustomAmountInput";

const BudgetCalc = (props: any) => {
  const {
    setBudgetCalculatorIncome,
    setIncomeAndExpense,
    needsPaymentAssistance,
    paymentAssistanceMessage,
    headingRef,
  } = props;
  const [mouseHover, setMouseHover] = useState<string | null>(null);
  const [income, setIncome] = useState<string[]>([
    "Salary/Wages",
    "Other Income",
  ]);
  const [expense, setExpense] = useState<string[]>([
    "Housing",
    "Transportation",
  ]);
  const [incomeValues, setIncomeValues] = useState<Record<string, number>>({});
  const [expenseValues, setExpenseValues] = useState<Record<string, number>>(
    {}
  );

  const handleIncomeChange = (label: string, value: number) => {
    setIncomeValues((prev) => ({ ...prev, [label]: value }));
  };

  const handleExpenseChange = (label: string, value: number) => {
    setExpenseValues((prev) => ({ ...prev, [label]: value }));
  };

  const handleRemoveIncome = (label: string) => {
    setIncome((prev) => prev.filter((item) => item !== label));
    setIncomeValues((prev) => {
      const newValues = { ...prev };
      delete newValues[label];
      return newValues;
    });
  };

  const handleRemoveExpense = (label: string) => {
    setExpense((prev) => prev.filter((item) => item !== label));
    setExpenseValues((prev) => {
      const newValues = { ...prev };
      delete newValues[label];
      return newValues;
    });
  };

  // Calculate total income and expense
  const totalIncome: number = useMemo(() => {
    return (
      Object.values(incomeValues).reduce((a: number, b: number) => a + b, 0) ||
      0
    );
  }, [incomeValues]);

  const totalExpense: number = useMemo(() => {
    return (
      Object.values(expenseValues).reduce((a: number, b: number) => a + b, 0) ||
      0
    );
  }, [expenseValues]);

  const netIncome: number = useMemo(
    () => (totalIncome || 0) - (totalExpense || 0),
    [totalIncome, totalExpense]
  );

  useEffect(() => {
    if (netIncome !== 0) {
      setBudgetCalculatorIncome(Number(netIncome));
    }

    setIncomeAndExpense({
      income: totalIncome,
      expense: totalExpense,
      netIncome,
    });
  }, [netIncome, totalIncome, totalExpense]);

  return (
    <>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="lg:text-xl font-medium text-txtPrimary lg:mb-10 mb-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue"
      >
        Budget calculator
      </h1>
      <div className="flex-col gap-5 flex">
        {/* income charts  */}
        <div className="flex justify-between 1xl:flex-nowrap flex-wrap lg:gap-20 gap-5 border border-strokePrimary lg:rounded-3xl rounded-2xl p-5">
          <div className="flex flex-col gap-8 w-fit">
            <div className="w-full">
              <p className="text-sm font-bold text-txtParagraph">
                Monthly Income
              </p>
              <p className="text-base font-bold text-txtPrimary">
                +{formatCurrency(totalIncome) || "$0.00"}
              </p>
            </div>
            <div className="border-b pb-8 w-full">
              <p className="text-sm font-bold text-txtParagraph">
                Monthly Expense
              </p>
              <p className="text-base font-bold text-txtPrimary">
                {"("}
                {formatCurrency(totalExpense) || "$0.00"}
                {")"}
              </p>
            </div>
            <div>
              <p className="text-base text-txtPrimary font-bold">Net Income</p>
              <p
                className={`text-base font-bold ${
                  netIncome >= 0 ? "text-green" : "text-red"
                }`}
              >
                {formatCurrency(netIncome) || "$0.00"}
              </p>
            </div>
          </div>
          <div>
            <BudgetChart
              incomeValues={incomeValues}
              expenseValues={expenseValues}
              income={income}
              expenses={expense}
            />
          </div>
        </div>
        {needsPaymentAssistance && (
          <p className="text-red text-xs" role="alert">
            {paymentAssistanceMessage}
          </p>
        )}
        {/* Monthly Income */}
        <div className="border border-strokePrimary rounded-lg min-h-[228px]">
          <p className="flex justify-between lg:px-5 px-4 py-2 lg:text-xl font-medium text-txtPrimary bg-bgSecondary rounded-tl-lg rounded-tr-lg">
            Monthly Income
            <span className="text-sm inline-flex items-center gap-2">
              <SelectAllDropDown
                options={BUDGET_INCOME_OPTION}
                checked={income}
                setChecked={setIncome}
                label="Add new"
                budgetCalculator
                isRight
                defaultChecked={["Salary/Wages", "Other Income"]}
              />
            </span>
          </p>
          <div className="lg:p-5 px-4 py-6">
            {income.map((label) => (
              <div
                key={label}
                className="flex lg:flex-row flex-col gap-4 items-end lg:mb-8 mb-6"
              >
                <Slider
                  value={incomeValues[label] || 0}
                  className=" w-full"
                  onChange={(val) => handleIncomeChange(label, val)}
                  minValue={0}
                  maxValue={50000}
                >
                  <Label className="text-base font-medium mb-4 text-txtParagraph block">
                    {label}
                  </Label>

                  <SliderTrack className="slider-track">
                    <div
                      className="slider-track-filled"
                      style={{
                        width: `${((incomeValues[label] || 0) / 50000) * 100}%`,
                      }}
                    ></div>
                    <SliderThumb className="slider-thumb" />
                  </SliderTrack>
                </Slider>
                <span className="flex justify-end items-center gap-4">
                  <CustomAmountInput
                    isError={false}
                    isDollar={true}
                    inputValue={(incomeValues[label] || "").toString()}
                    setinputValue={(val: any) => {
                      handleIncomeChange(label, Number(val));
                    }}
                    disabled={false}
                    checkAmt={false}
                    className={`!pl-[23px]`}
                    showErr={false}
                    isBudgetCalc={true}
                    _className="!text-txtParagraph"
                  />

                  <div
                    onClick={() => handleRemoveIncome(label)}
                    onMouseOver={() => setMouseHover(`income-${label}`)}
                    onMouseOut={() => setMouseHover(null)}
                  >
                    <DeleteIcon
                      className="cursor-pointer group"
                      fillColor={
                        mouseHover === `income-${label}`
                          ? "var(--color-red)"
                          : "var(--color-icon-bright)"
                      }
                    />
                  </div>
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* Monthly Expense */}
        <div className="border border-strokePrimary rounded-lg min-h-[228px]">
          <p className="flex justify-between lg:px-5 px-4 py-2 lg:text-xl font-medium text-txtPrimary bg-bgSecondary rounded-tl-lg rounded-tr-lg">
            Monthly Expense
            <span className="text-sm inline-flex items-center gap-2">
              <SelectAllDropDown
                options={BUDGET_EXPENSE_OPTION}
                checked={expense}
                setChecked={setExpense}
                label="Add new"
                budgetCalculator
                isRight
                defaultChecked={["Housing", "Transportation"]}
              />
            </span>
          </p>
          <div className="lg:p-5 px-4 py-6">
            {expense.map((label) => (
              <div
                key={label}
                className="flex gap-4  lg:flex-row flex-col items-end lg:mb-8 mb-6"
              >
                <Slider
                  value={expenseValues[label] || 0}
                  className=" w-full"
                  onChange={(val) => handleExpenseChange(label, val)}
                  minValue={0}
                  maxValue={10000}
                >
                  <Label className="text-base font-medium mb-4 text-txtParagraph block">
                    {label} Expense
                  </Label>
                  <SliderTrack className="slider-track">
                    <div
                      className="slider-track-filled"
                      style={{
                        width: `${
                          ((expenseValues[label] || 0) / 10000) * 100
                        }%`,
                      }}
                    ></div>
                    <SliderThumb className="slider-thumb" />
                  </SliderTrack>
                </Slider>
                <span className="flex justify-end items-center gap-4">
                  <CustomAmountInput
                    isError={false}
                    isDollar={true}
                    inputValue={(expenseValues[label] || "").toString()}
                    setinputValue={(val: any) => {
                      handleExpenseChange(label, Number(val));
                    }}
                    disabled={false}
                    checkAmt={false}
                    className={`!pl-[23px]`}
                    showErr={false}
                    isBudgetCalc={true}
                    _className="!text-txtParagraph"
                  />

                  <div
                    onClick={() => handleRemoveExpense(label)}
                    onMouseOver={() => setMouseHover(`expense-${label}`)}
                    onMouseOut={() => setMouseHover(null)}
                  >
                    <DeleteIcon
                      className="cursor-pointer group"
                      fillColor={
                        mouseHover === `expense-${label}`
                          ? "var(--color-red)"
                          : "var(--color-icon-bright)"
                      }
                    />
                  </div>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default BudgetCalc;
